/**
 * WebRTCConnector 模块负责管理远程控制 WebRTC 连接及数据通道。
 */
import {
	sendCandidate,
	sendIceRestartOffer,
} from "@/services/remote/signaling";
import { useWebRTCStore } from "@/stores/remote";
import {
	handleFileChannelMessage,
	resetFileTransferState,
	sendFileHello,
} from "../file-transfer";
import { sendHello, sendReleaseAll } from "./keyboard";
import { handleControlMessage, resetNegotiationState } from "./negotiation";
import { getWebRTCConfig, setIceConfigUpdateCallback } from "./peer-config";
import { initResolutionChecker } from "./screen";

const getCurrentPeer = () => WebRTCConnector.getInstance();

class WebRTCConnector {
	static instance: WebRTCConnector | null = null;

	local_candidates: RTCIceCandidateInit[];
	peer: RTCPeerConnection | null;
	stream: MediaStream | null;
	onStreamChange: ((stream: MediaStream | null) => void) | null;
	dc_control: RTCDataChannel | null;
	dc_cursor: RTCDataChannel | null;
	dc_file: RTCDataChannel | null;
	// disconnected → 浏览器内部 ICE 协商常会自动恢复到 connected，
	// 直接 destroy 会把恢复路径切掉。给一次 grace 等待，超时仍未恢复才 destroy。
	disconnectedGraceTimer: ReturnType<typeof setTimeout> | null = null;

	static getInstance() {
		if (!WebRTCConnector.instance) {
			WebRTCConnector.instance = new WebRTCConnector();
		}
		return WebRTCConnector.instance;
	}

	constructor() {
		// 检查是否已经存在实例
		if (WebRTCConnector.instance) {
			throw new Error(
				"WebRTCConnector is a singleton. Use getInstance() method.",
			);
		}

		WebRTCConnector.instance = this;

		// 字段默认值必须在 init() 之前设好：init() → initDataChannels() 会把
		// dc_control / dc_cursor / dc_file 赋成真实通道；若放在 init()
		// 之后执行 `this.dc_* = null`，会把刚建好的通道覆盖成 null，导致首次连接时
		// RemoteScreen 取到的通道为 null、鼠标键盘事件全部发不出去。
		// （断开重连走 createOffer/reinitialize 的 init()，其后不再覆盖，故"重连就好"。）
		this.local_candidates = [];
		this.stream = null;
		this.onStreamChange = null;
		this.dc_control = null;
		this.dc_cursor = null;
		this.dc_file = null;

		// 使用动态配置创建 RTCPeerConnection，并在 init() 内建立 data channel
		this.peer = new RTCPeerConnection(getWebRTCConfig());
		this.init();

		// 设置 ICE 配置更新回调
		setIceConfigUpdateCallback(() => {
			this.reinitializeWithNewIceConfig();
		});

		// 初始化时更新状态
		this.updateStoreStates();
	}

	async getIdentityAssertion() {
		try {
			if (!this.peer) {
				throw new Error("Peer connection not available");
			}
			const identityPromise = this.peer.peerIdentity;
			if (!identityPromise) {
				throw new Error("Peer identity not available");
			}
			const identity = await identityPromise;
			console.log("[webrtc] identity updated ===>", identity);
			useWebRTCStore.getState().setPeerIdentity(identity);
			return identity;
		} catch (err) {
			console.error("[webrtc] Error identifying remote peer: ", err);
			useWebRTCStore.getState().setError("Failed to get peer identity");
			return null;
		}
	}

	updateStoreStates() {
		if (!this.peer) {
			console.log("[webrtc] peer not initialized");
			return;
		}

		const store = useWebRTCStore.getState();
		console.log("更新 WebRTC 状态:", {
			signalingState: this.peer.signalingState,
			iceConnectionState: this.peer.iceConnectionState,
			connectionState: this.peer.connectionState,
			hasStream: !!this.stream,
			streamDetails: this.stream
				? {
						active: this.stream.active,
						id: this.stream.id,
						tracks: this.stream.getTracks().map((t) => ({
							kind: t.kind,
							enabled: t.enabled,
							readyState: t.readyState,
						})),
					}
				: null,
		});

		// 更新所有状态
		store.setSignalingState(this.peer.signalingState);
		store.setIceConnectionState(this.peer.iceConnectionState);
		store.setConnectionState(this.peer.connectionState);

		// 确保流的更新是最后一个，这样可以触发依赖于流的组件更新
		if (this.stream) {
			console.log("更新视频流到 store");
			store.setStream(this.stream);
		}
	}

	initDataChannels() {
		// 远程输入协议 v2 §3：control 通道承载键盘 / 文本 / 鼠标按键 / 滚轮 /
		// 模式 / 释放 / 剪贴板 / 握手等需要可靠有序的消息。
		// onmessage 必须接入 negotiation 以记录 receiver hello（capability gating 的前置条件）。
		resetNegotiationState();
		resetFileTransferState();
		this.dc_control = this.setupDataChannel(
			"control",
			{ ordered: true },
			// 协议 §5：data channel open 后**必须**首先发送 hello。
			// 放在连接器层而不是 RemoteScreen 组件层，是为了在 ICE reinit /
			// 重建 peer 时也能自动重发 hello——组件那边的 effect 只跑一次，
			// 接不上新 channel 的 open 事件。
			() => {
				console.log("control channel has opened");
				sendHello(this.dc_control);
			},
			() => console.log("control channel has closed"),
			handleControlMessage,
		);

		// 远程输入协议 v2 §3：cursor 通道承载高频鼠标位移，允许丢包 / 乱序。
		this.dc_cursor = this.setupDataChannel(
			"cursor",
			{ ordered: false, maxRetransmits: 0 },
			() => console.log("cursor channel has opened"),
			() => console.log("cursor channel has closed"),
			(e) => console.log(`Message from cursor channel,`, e),
		);

		const fileChannel = this.setupDataChannel(
			"file",
			{ ordered: true },
			// §7.1:通道 open 后立即发 file:hello(控制端)
			() => sendFileHello(fileChannel),
			() => resetFileTransferState(),
			handleFileChannelMessage,
		);
		// §4:必须设 arraybuffer,否则二进制 chunk 帧落到 Blob、§6.2 uint32be 头解析失败
		fileChannel.binaryType = "arraybuffer";
		this.dc_file = fileChannel;
		// v1 不创建 query 通道:远端目录浏览 / 拉取(§2)不做。
	}

	setupDataChannel = (
		channelId: string,
		options: RTCDataChannelInit,
		onOpen: () => void,
		onClose: () => void,
		onMessage: (event: MessageEvent) => void,
	) => {
		if (!this.peer) {
			throw new Error("Peer connection not available");
		}
		const channel = this.peer.createDataChannel(channelId, options);
		channel.onopen = onOpen;
		channel.onclose = onClose;
		channel.onmessage = onMessage;
		return channel;
	};
	/**
	 * 处理ICE候选者事件
	 * 当获取到候选者时，此函数会记录并处理候选者信息
	 * 如果候选者为null，则表示本地候选者已结束
	 * 否则，将候选者信息推送并存储到本地候选者数组中
	 */

	/* NOTE 务必复查 local_candidates, 避免signalingState 还不是stable的时候，candidate已经结束，导致遗留candidate未发送 */
	handleIceCandidate(event: RTCPeerConnectionIceEvent) {
		console.log("[webrtc] handleIceCandidate", event);
		if (!this.peer) return;
		console.log("[webrtc] current signalingState", this.peer.signalingState);
		if (event.candidate === null) {
			console.log("local candidate finished");
			// 如果候选者为null，则表示本地候选者已结束

			this.checkRemainingCandidates();
		} else if (this.peer.signalingState === "stable") {
			sendCandidate(event.candidate);
		} else {
			this.local_candidates.push(event.candidate.toJSON());
		}
	}

	checkRemainingCandidates() {
		console.log(
			"[webrtc] checkRemainingCandidates",
			this.local_candidates.length,
		);
		// 将本地候选者信息推送到 signal server
		while (this.local_candidates.length > 0) {
			sendCandidate(this.local_candidates.pop() as RTCIceCandidateInit);
		}
	}

	handleOnTrack(event: RTCTrackEvent) {
		console.log("收到视频流 ===>", {
			event: event,
			streams: event.streams,
			track: {
				kind: event.track.kind,
				enabled: event.track.enabled,
				readyState: event.track.readyState,
				muted: event.track.muted,
			},
		});

		const stream = event.streams[0];
		if (!stream) {
			console.error("未收到视频流");
			return;
		}

		// 确保旧的流被正确清理
		if (this.stream) {
			try {
				this.stream.getTracks().forEach((track) => {
					track.stop();
				});
			} catch (error) {
				console.warn("Error stopping old stream tracks:", error);
			}
		}

		this.stream = stream;
		// 直接更新到 store
		console.log("立即更新视频流到 store");
		useWebRTCStore.getState().setStream(stream);
		this.updateStoreStates();
	}

	handleIceConnectionStateChange(event: Event) {
		const target = event.target as RTCPeerConnection;
		const state = target.iceConnectionState;
		useWebRTCStore.getState().setIceConnectionState(state);

		// 任意状态发生变化都先撤掉待执行的宽限 destroy；
		// 若新状态本身仍是 disconnected，下面会再排上。
		if (this.disconnectedGraceTimer) {
			clearTimeout(this.disconnectedGraceTimer);
			this.disconnectedGraceTimer = null;
		}

		if (state === "failed" || state === "closed") {
			console.warn(`[webrtc] iceConnection ${state}, destroying connection.`);
			this.destroy();
			return;
		}

		if (state === "disconnected") {
			// grace 期间不动连接，让 ICE 自行恢复；超时仍未回到健康态再 destroy。
			console.warn(
				"[webrtc] iceConnection disconnected, waiting for recovery before teardown.",
			);
			this.disconnectedGraceTimer = setTimeout(() => {
				this.disconnectedGraceTimer = null;
				const current = this.peer?.iceConnectionState;
				if (current === "disconnected" || current === "failed") {
					console.warn(
						`[webrtc] iceConnection still ${current} after grace period, destroying.`,
					);
					this.destroy();
				}
			}, 8000);
		}
	}

	handleSignalingStateChange = () => {
		if (!this.peer) return;
		useWebRTCStore.getState().setSignalingState(this.peer.signalingState);
		if (this.peer.signalingState === "stable") {
			this.checkRemainingCandidates();
		}
	};

	handleConnectionStateChange = () => {
		if (!this.peer) return;
		useWebRTCStore.getState().setConnectionState(this.peer.connectionState);
	};

	async createOffer() {
		console.log("[webrtc] createOffer");
		try {
			// 检查 peer 连接是否存在，如果不存在则重新初始化
			if (!this.peer) {
				console.log("[webrtc] Peer connection is null, reinitializing...");
				this.peer = new RTCPeerConnection(getWebRTCConfig());
				this.init();
			}

			const offer = await this.peer.createOffer();
			await this.peer.setLocalDescription(offer);
			return offer;
		} catch (error) {
			console.error("[webrtc] Create offer failed:", error);
			useWebRTCStore.getState().setError("Failed to create offer");
			throw error;
		}
	}

	init() {
		console.log("[webrtc] Initializing WebRTC connection...");

		try {
			if (!this.peer) {
				throw new Error("Peer connection not available");
			}
			this.initDataChannels();

			// 确保视频接收配置正确
			const transceiver = this.peer.addTransceiver("video", {
				direction: "recvonly",
				streams: [],
			});

			console.log("[webrtc] Video transceiver configured:", {
				currentDirection: transceiver.currentDirection,
				direction: transceiver.direction,
				sender: transceiver.sender,
				receiver: transceiver.receiver,
			});

			// 设置事件处理器
			this.peer.onicecandidate = this.handleIceCandidate.bind(this);
			this.peer.ontrack = this.handleOnTrack.bind(this);
			this.peer.addEventListener(
				"iceconnectionstatechange",
				this.handleIceConnectionStateChange.bind(this),
			);
			this.peer.addEventListener(
				"signalingstatechange",
				this.handleSignalingStateChange.bind(this),
			);
			this.peer.addEventListener(
				"connectionstatechange",
				this.handleConnectionStateChange.bind(this),
			);

			// 添加连接协商失败的处理
			this.peer.onnegotiationneeded = (event) => {
				console.log("[webrtc] Negotiation needed:", event);
			};

			// this.createOffer();
			this.updateStoreStates();

			console.log("[webrtc] WebRTC connection initialized successfully");
		} catch (error) {
			console.error("[webrtc] Error during initialization:", error);
			throw error;
		}
	}

	getDataChannel() {
		return this.dc_control;
	}

	getCursorChannel() {
		return this.dc_cursor;
	}

	getFileChannel() {
		return this.dc_file;
	}

	getPeerConnection() {
		return this.peer;
	}

	handleRemoteAnswer(content: string) {
		try {
			if (!this.peer) {
				throw new Error("Peer connection not available");
			}

			console.log("[webrtc] Setting remote description from answer");
			this.peer.setRemoteDescription(
				new RTCSessionDescription({ type: "answer", sdp: content }),
			);
		} catch (error) {
			console.error("[webrtc] Error setting remote description:", error);
			throw error;
		}
	}

	/**
	 * 重新初始化 WebRTC 连接，使用最新的 ICE 配置
	 */
	async reinitializeWithNewIceConfig() {
		console.log("Reinitializing WebRTC with new ICE configuration...");

		// 销毁当前连接
		this.destroy();

		// 重新创建连接，使用最新的 ICE 配置
		this.peer = new RTCPeerConnection(getWebRTCConfig());
		this.init();

		console.log("WebRTC reinitialized with new ICE configuration");
	}

	/**
	 * 执行 ICE restart
	 * @returns {Promise<void>}
	 */
	async restartIce() {
		try {
			console.log("[webrtc] Starting ICE restart...");

			if (!this.peer) {
				throw new Error("Peer connection not available");
			}

			// 检查连接状态
			if (this.peer.connectionState === "closed") {
				throw new Error("Connection is closed, cannot restart ICE");
			}

			// 创建 ICE restart offer
			const offer = await this.peer.createOffer({ iceRestart: true });
			console.log("[webrtc] Created ICE restart offer:", offer);

			// 设置本地描述
			await this.peer.setLocalDescription(offer);
			console.log("[webrtc] Set local description for ICE restart");

			// 发送 ICE restart offer
			await sendIceRestartOffer(offer);

			console.log("[webrtc] ICE restart initiated successfully");
		} catch (error) {
			console.error("[webrtc] ICE restart failed:", error);
			throw error;
		}
	}

	/**
	 * 处理 ICE restart offer
	 * @param {string} sdp - SDP 内容
	 * @returns {Promise<RTCSessionDescription>} - 返回 answer
	 */
	async handleIceRestartOffer(sdp: string) {
		try {
			console.log("[webrtc] Handling ICE restart offer...");

			if (!this.peer) {
				throw new Error("Peer connection not available");
			}

			// 设置远程描述
			await this.peer.setRemoteDescription(
				new RTCSessionDescription({
					type: "offer",
					sdp: sdp,
				}),
			);
			console.log("[webrtc] Set remote description for ICE restart");

			// 创建 answer
			const answer = await this.peer.createAnswer();
			await this.peer.setLocalDescription(answer);
			console.log("[webrtc] Created and set local answer for ICE restart");

			return answer;
		} catch (error) {
			console.error("[webrtc] Failed to handle ICE restart offer:", error);
			throw error;
		}
	}

	/**
	 * 处理 ICE restart answer
	 * @param {string} sdp - SDP 内容
	 */
	async handleIceRestartAnswer(sdp: string) {
		try {
			console.log("[webrtc] Handling ICE restart answer...");

			if (!this.peer) {
				throw new Error("Peer connection not available");
			}

			// 设置远程描述
			await this.peer.setRemoteDescription(
				new RTCSessionDescription({
					type: "answer",
					sdp: sdp,
				}),
			);
			console.log("[webrtc] Set remote answer for ICE restart");
		} catch (error) {
			console.error("[webrtc] Failed to handle ICE restart answer:", error);
			throw error;
		}
	}

	cleanupResources() {
		console.log("[webrtc] Cleaning up resources...");

		// 需要清理的属性
		const channelProperties = ["dc_control", "dc_cursor", "dc_file"] as const;

		// 清理数据通道
		channelProperties.forEach((propName) => {
			const channel = this[propName];
			if (channel) {
				try {
					console.log(`[webrtc] Closing data channel: ${propName}`);
					channel.close();
				} catch (error) {
					console.warn(
						`[webrtc] Error closing data channel ${propName}:`,
						error,
					);
				}
				this[propName] = null;
			}
		});

		// 清理媒体流
		if (this.stream) {
			try {
				console.log("[webrtc] Stopping media stream tracks...");
				this.stream.getTracks().forEach((track) => {
					track.stop();
				});
			} catch (error) {
				console.warn("[webrtc] Error stopping media stream tracks:", error);
			}
			this.stream = null;
		}

		// 清理 peer 连接
		if (this.peer) {
			try {
				console.log("[webrtc] Closing peer connection...");
				this.peer.close();
			} catch (error) {
				console.warn("[webrtc] Error closing peer connection:", error);
			}
			this.peer = null;
		}

		console.log("[webrtc] Resource cleanup completed");
	}

	resetInstanceState() {
		console.log("[webrtc] Resetting instance state...");

		if (this.disconnectedGraceTimer) {
			clearTimeout(this.disconnectedGraceTimer);
			this.disconnectedGraceTimer = null;
		}

		this.local_candidates = [];
		this.stream = null;
		this.onStreamChange = null;
		resetNegotiationState();

		// 批量更新 store 状态
		useWebRTCStore.setState({
			iceConnectionState: "closed",
			connectionState: "closed",
			signalingState: "closed",
			stream: null,
		});

		console.log("[webrtc] Instance state reset completed");
	}

	destroy() {
		console.log("[webrtc] Starting destroy process...");

		// 主动断开 / 重建前先释放远端输入状态，避免鼠标按钮或键盘卡住。
		sendReleaseAll(this.dc_control);

		// 清理资源
		this.cleanupResources();

		// 重置实例状态，但不销毁实例本身
		this.resetInstanceState();

		console.log("[webrtc] Destroy completed, instance ready for reuse");
	}
}

// new section end

export const closeRemote = () => {
	getCurrentPeer().destroy();
};

export const currentDataChannel = () => {
	return getCurrentPeer().getDataChannel();
};

export const currentCursorChannel = () => {
	return getCurrentPeer().getCursorChannel();
};

export const currentFileChannel = () => {
	return getCurrentPeer().getFileChannel();
};

function getCandidatePair() {
	const pc = getCurrentPeer().getPeerConnection();
	if (!pc?.sctp?.transport?.iceTransport) {
		return null;
	}
	const pair = pc.sctp.transport.iceTransport.getSelectedCandidatePair();
	//console.log(pair.remote.type);
	return pair;
}

initResolutionChecker();

export { getCandidatePair, getCurrentPeer };

/**
 * 执行 ICE restart
 * @returns {Promise<void>}
 */
export const restartIce = async () => {
	try {
		const peer = getCurrentPeer();
		await peer.restartIce();
	} catch (error) {
		console.error("[webrtc] ICE restart failed:", error);
		throw error;
	}
};
